import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { 
  Cart, 
  CartItem, 
  CartContextType, 
  CartItemCreateData,
  createEmptyCart
} from '../types';
import { cartService } from '../services/cartService';
import { cartEventListener } from '../services/cartEventListener';
import { CartOperations } from '../services/cartOperations';

// Cart action types
type CartAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CART'; payload: Cart }
  | { type: 'ADD_ITEM'; payload: CartItemCreateData }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_QUANTITY'; payload: { itemId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART' };

// Cart state interface
interface CartState {
  cart: Cart;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: CartState = {
  cart: createEmptyCart(),
  loading: true,
  error: null
};

// Cart reducer
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
        error: action.payload ? null : state.error // Clear error when loading starts
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };

    case 'SET_CART':
      return {
        ...state,
        cart: action.payload,
        loading: false,
        error: null
      };

    case 'LOAD_CART':
      return {
        ...state,
        loading: false,
        error: null
      };

    default:
      return state;
  }
};

// Create context
const CartContext = createContext<CartContextType | undefined>(undefined);

// Cart provider props
interface CartProviderProps {
  children: ReactNode;
}

// Cart provider component
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [operations, setOperations] = React.useState<CartOperations | null>(null);

  // Initialize cart operations
  useEffect(() => {
    const cartOps = new CartOperations();
    cartOps.loadCart();
    
    setOperations(cartOps);
    
    // Load cart from localStorage without immediately updating VAT
    // VAT calculations will be handled by individual pages when needed
    const currentCart = cartOps.getCart();
    console.log('🔄 CartContext - Cart loaded from localStorage, shipping preserved:', currentCart.shipping);
    dispatch({ type: 'SET_CART', payload: currentCart });
    dispatch({ type: 'SET_LOADING', payload: false });
  }, []);

  // Handle cart changes from other tabs
  useEffect(() => {
    const handleCartChange = (cart: Cart | null) => {
      if (cart) {
        dispatch({ type: 'SET_CART', payload: cart });
      } else {
        dispatch({ type: 'SET_CART', payload: createEmptyCart() });
      }
    };

    cartEventListener.addListener(handleCartChange);

    return () => {
      cartEventListener.removeListener(handleCartChange);
    };
  }, []);

  // VAT calculations are now only run when cart page loads, not periodically
  // This prevents interference with shipping calculations during checkout

  // Cart operations
  const addItem = useCallback(async (itemData: CartItemCreateData) => {
    if (!operations) return;
    
    try {
      await operations.addItem(itemData);
      dispatch({ type: 'SET_CART', payload: operations.getCart() });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to add item to cart' 
      });
    }
  }, [operations]);

  const removeItem = useCallback(async (itemId: string) => {
    if (!operations) return;
    
    try {
      await operations.removeItem(itemId);
      dispatch({ type: 'SET_CART', payload: operations.getCart() });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to remove item from cart' 
      });
    }
  }, [operations]);

  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    if (!operations) return;
    
    try {
      await operations.updateQuantity(itemId, quantity);
      dispatch({ type: 'SET_CART', payload: operations.getCart() });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to update item quantity' 
      });
    }
  }, [operations]);

  const clearCart = useCallback(async () => {
    if (!operations) return;
    
    try {
      await operations.clearCart();
      dispatch({ type: 'SET_CART', payload: operations.getCart() });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to clear cart' 
      });
    }
  }, [operations]);

  const getCart = useCallback(() => {
    return state.cart;
  }, [state.cart]);

  const getItemCount = useCallback(() => {
    return state.cart.totalItems;
  }, [state.cart.totalItems]);

  const getTotalPrice = useCallback(() => {
    return state.cart.total;
  }, [state.cart.total]);

  // Clear error function
  const clearError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  // Update cart with VAT calculations
  const updateCartWithVat = useCallback(async () => {
    if (!operations) return;
    
    try {
      console.log('🔄 CartContext.updateCartWithVat - Starting VAT update...');
      // Use CartOperations' updateCartWithVat which respects admin VAT settings
      await operations.updateCartWithVat();
      // Get the updated cart after VAT calculation
      const updatedCart = operations.getCart();
      dispatch({ type: 'SET_CART', payload: updatedCart });
      dispatch({ type: 'SET_ERROR', payload: null });
      console.log('🔄 CartContext.updateCartWithVat - Cart updated successfully');
    } catch (error) {
      console.error('Error updating cart with VAT:', error);
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to update cart with VAT' 
      });
    }
  }, [operations]);

  const applyCoupon = useCallback(async (couponCode: string) => {
    if (!operations) return false;
    
    try {
      const success = await operations.applyCoupon(couponCode);
      dispatch({ type: 'SET_CART', payload: operations.getCart() });
      dispatch({ type: 'SET_ERROR', payload: null });
      return success;
    } catch (error) {
      // Don't set global error state for coupon errors - let the component handle them locally
      // Just return false to indicate failure
      return false;
    }
  }, [operations]);

  const removeCoupon = useCallback(async () => {
    if (!operations) return;
    
    try {
      await operations.removeCoupon();
      dispatch({ type: 'SET_CART', payload: operations.getCart() });
      dispatch({ type: 'SET_ERROR', payload: null });
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to remove coupon' 
      });
    }
  }, [operations]);

  const clearVatCache = useCallback(() => {
    if (!operations) return;
    
    try {
      operations.clearVatCache();
      console.log('🔄 VAT cache cleared manually');
    } catch (error) {
      console.error('Failed to clear VAT cache:', error);
    }
  }, [operations]);

  // Context value
  const contextValue: CartContextType = {
    cart: state.cart,
    operations: {
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getCart,
      getItemCount,
      getTotalPrice,
      updateCartWithVat,
      applyCoupon,
      removeCoupon,
      clearVatCache
    },
    loading: state.loading,
    error: state.error
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  
  return context;
};

// Custom hook for cart operations only
export const useCartOperations = () => {
  const { operations } = useCart();
  return operations;
};

// Custom hook for cart state only
export const useCartState = () => {
  const { cart, loading, error } = useCart();
  return { cart, loading, error };
};

// Custom hook for cart item count
export const useCartItemCount = (): number => {
  const { cart } = useCart();
  return cart.totalItems;
};

// Custom hook for cart total price
export const useCartTotalPrice = (): number => {
  const { cart } = useCart();
  return cart.total;
};

// Custom hook for cart loading state
export const useCartLoading = (): boolean => {
  const { loading } = useCart();
  return loading;
};

// Custom hook for cart error state
export const useCartError = (): string | null => {
  const { error } = useCart();
  return error;
};

// Custom hook to check if cart is empty
export const useIsCartEmpty = (): boolean => {
  const { cart } = useCart();
  return cart.items.length === 0;
};

// Custom hook for cart summary
export const useCartSummary = () => {
  const { cart } = useCart();
  return {
    itemCount: cart.items.length,
    totalItems: cart.totalItems,
    subtotal: cart.subtotal,
    vat: cart.vat,
    vatPercentage: cart.vatPercentage,
    isInclusive: cart.isInclusive,
    shipping: cart.shipping,
    couponDiscount: cart.couponDiscount || 0,
    total: cart.total,
    isEmpty: cart.items.length === 0
  };
};

// Export context for testing
export { CartContext };
